/* global window require console */
import Vue from "vue";
import VueMq from "vue-mq";
import debounce from "debounce";
import equal from "fast-deep-equal";
import "../../../../third-party/microscope_app";
import "./image-viewer-ruler";
import "./image-viewer-marker";
import "./image-viewer-fullscreen-toggle";
import EventBus from "./event-bus";
import { iconButton, iconButtonTitle } from "./image-viewer-icon-button";
import styleSettings from "../settings.scss";


// Enumerate 3p zoom widget modes
const NATIVE_ZOOM_WIDGET_MODE = {
  FULL: 0,
  HIDDEN: 3
};

// Enumerate breakpoints
const BREAKPOINT = {
  SMALL: "small",
  MEDIUM: "medium",
  LARGE: "large"
};

// Register media query plugin
Vue.use(VueMq, {
  // Define breakpoints (see `src/frontend/common/settings.scss`)
  breakpoints: {
    [BREAKPOINT.SMALL]: parseInt(styleSettings.breakpointSmall),
    [BREAKPOINT.MEDIUM]: parseInt(styleSettings.breakpointMedium),
    [BREAKPOINT.LARGE]: Infinity,
  }
});

export default Vue.component("image-viewer", {
  props: [
    // URL to load from the Google image BE
    "url",
    // Flag wether to render a ruler (requires setting `resolution`)
    "ruler",
    // Pixels per cm if available
    "resolution",
    // Fragment markers map
    "markers",
    // Flag to switch to mini zoom UI (used in Featured scrolls)
    "compact",
    // Initial viewport to load (used in Featured scrolls)
    "viewport",
    // HREF to an archive image page (used in Featured scrolls)
    "href"
  ],

  directives: {
    // Turn any `<a>` or `<button>` into an icon button
    iconButton,
    // Easily add translated title to icon buttons
    title: iconButtonTitle
  },

  data() {
    return {
      // The 3p viewer instance
      microscopeViewer: null,
      // Is viewport state available yet
      isViewportReady: false,
      // Is the viewer in fullscreen mode
      isFullscreen: false,
      // Is mouse currently over the extra UI layer
      mouseIsOverExtraUI: false,
      // Is the viewer in idle mode
      // Initially false unless in compact mode
      isIdle: this.compact,
      // Is a fragment in focus due to an external event
      hasExternallyHighlightedFragment: false,
      // Store image metadata once loaded
      imageMetadata: null,
      // Current scale due to zooming
      currentScale: NaN,
      // Is the entire image shown in the viewport
      isFullyZoomedOut: false,
      // Current pan (left, top)
      currentPan: {
        left: NaN,
        top: NaN
      },
      // Is this device has touch (only true after first touch event)
      hasTouch: false,
      // Is a touch scroll happening (single touch move)
      isTouchScrolling: false,
      // An internal el of the 3p viewer which is referenced a lot
      microscopeGlassElement: null,
      // Store useful class names
      classNames: {
        // This component's root element
        imageViewer: "c-image-viewer",
        // This component's root element
        imageViewerFullscreen: "c-image-viewer--fullscreen",
        // Placeholder for third party viewer
        microscopeViewer: "c-microscope-viewer",
        // Internal layer in the 3p viewer which is accessed later
        microscopeGlass: "m2-glassLayer",
        // Layer to render UI on top of 3p viewer
        extras: "c-image-viewer__extras",
        // Modifier to hide `extras` layer
        extrasHidden: "c-image-viewer__extras--hidden",
        // Bottom button bar
        buttonBarBottom: "c-image-viewer-button-bar c-image-viewer-button-bar--bottom",
        // Bottom button top
        buttonBarTop: "c-image-viewer-button-bar c-image-viewer-button-bar--top",
        // Markers map
        markers: "c-image-viewer-markers"
      },
    };
  },

  computed: {
    // Main component classes
    classes() {
      return {
        [this.classNames.imageViewer]: true,
        [this.classNames.imageViewerFullscreen]: this.isFullscreen
      };
    },

    // Class binding for `extras` layer
    extrasClasses() {
      return {
        [this.classNames.extras]: true,
        [this.classNames.extrasHidden]: !this.extrasAreVisible
      };
    },

    // Get initial viewport (from props) as MicroscopeState
    initialViewport() {
      if (this.viewport) {
        return new window.MicroscopeState(
          this.viewport.x,
          this.viewport.y,
          this.viewport.z,
          this.viewport.size);
      }
    },

    // Should the extra UI layer (ruler, markers etc.) be visible?
    extrasAreVisible() {
      // Show when a fragment is in focus due to external event
      if (this.hasExternallyHighlightedFragment) {
        return true;
      }
      // The rest of the time show when not in idle mode
      return !this.isIdle;
    },

    // Get the desired 3p viewer's zoom widget mode
    nativeZoomWidgetMode() {
      if (this.extrasAreVisible && this.breakpoint == BREAKPOINT.LARGE) {
        if (!this.compact || this.isFullscreen) {
          return NATIVE_ZOOM_WIDGET_MODE.FULL;
        }
      }
      return NATIVE_ZOOM_WIDGET_MODE.HIDDEN;
    },

    // Set wether our custom mini zoom should be shown
    miniZoomWidgetVisible() {
      return this.compact && !this.isFullscreen;
    },

    // Set wether ruler should be shown
    rulerIsVisible() {
      // Only continue if ruler property is defined and viewer is ready to render it
      if (!this.$props.ruler || !this.isViewportReady) {
        return false;
      }
      // On small screens show only in fullscreen
      else if(this.breakpoint === BREAKPOINT.SMALL) {
        return this.isFullscreen;
      }
      // Otherwise show it
      else {
        return true;
      }
    },

    markersVisible() {
      return this.markers && this.breakpoint != BREAKPOINT.SMALL;
    },

    // Should the viewer zoom/pan on mouse wheel events
    allowMouseWheelZoom() {
      // Compact instances (i.e Featured Scrolls) and small screen sizes
      // should only allow gestures in fullscreen mode
      if (this.compact || this.breakpoint === BREAKPOINT.SMALL) {
        return this.isFullscreen;
      }
      // On larger touch devices wait for first tap and only then allow it,
      // to prevent scroll traps
      else if (this.hasTouch) {
        return !(this.isIdle || this.compact);
      }
      // On large devices with a mouse always allow it
      return true;
    },

    // Should the viewer take over single touch move events for panning.
    // We sometime prevent it to avoid scroll traps on mobile.
    allowTouchPan() {
      return !this.isIdle && !this.isFullyZoomedOut;
    },

    // Breakpoint according to the MediaQuery plugin
    breakpoint() {
      return this.$mq;
    }
  },

  watch: {
    nativeZoomWidgetMode(value) {
      // Update the 3p viewer
      if (this.microscopeViewer) {
        this.microscopeViewer.SetZoomWidgetVisibility(value);
      }
    },

    allowMouseWheelZoom(value) {
      this.updateMicroscopeScrollPolicy(value);
    }
  },

  mounted() {
    this.initMicroscopeViewer();
    this.initExternalEventHandlers();
    this.loadImage();
  },

  methods: {
    // Init and configure 3p viewer
    initMicroscopeViewer() {
      // Select placeholder element
      const microscopeContainer = this.$refs.microscopeViewerEl;
      // instantiate 3p viewer
      this.microscopeViewer = new window.Microscope(microscopeContainer, false);

      // Configure 3p viewer
      this.microscopeViewer.SetGlobalKeyEventsActive(true);
      this.microscopeViewer.SetLoadCallback(this.onImageLoaded);
      this.microscopeViewer.SetStateChangeCallback(this.onViewportChange);
      // Prevent FOUC:
      this.microscopeViewer.SetZoomWidgetVisibility(NATIVE_ZOOM_WIDGET_MODE.HIDDEN);
    },

    // Init external events bus handlers
    initExternalEventHandlers() {
      const self = this;
      // Handle interactions with external fragment list
      EventBus.$on(["fragmentList.fragment.focus", "fragmentList.focus"], () => {
        self.hasExternallyHighlightedFragment = true;
      });
      // Handle interaction end
      EventBus.$on("fragmentList.blur", () => {
        self.hasExternallyHighlightedFragment = false;
      });
    },

    loadImage() {
      if (this.url) {
        this.microscopeViewer.LoadImage(this.url, this.initialViewport);
      }
    },

    onImageLoaded() {
      // Set position and size of zoom widget:
      this.microscopeViewer.SetZoomWidgetLayout({
        "top": 14,
        "right": 14
      }, 80);
      // Show zoom widget if needed
      this.microscopeViewer.SetZoomWidgetVisibility(this.nativeZoomWidgetMode);
      // Store image metadata
      this.imageMetadata = this.microscopeViewer.GetImageMetadata();
      // Jump to initial viewport if defined
      if (this.initialViewport !== undefined) {
        this.microscopeViewer.AnimateTo(this.initialViewport, 0);
      }
      // Update ref to newly created el
      this.microscopeGlassElement = this.$el.querySelector(
        `.${ this.classNames.microscopeViewer }>.${ this.classNames.microscopeGlass }`);
      // Set wether 3p viewer should catch interaction events
      this.updateMicroscopeScrollPolicy(this.allowMouseWheelZoom);
      // Start idle timer
      this.onInteractionComplete();
    },

    // Zoom in one step
    zoomIn() {
      const { x, y, z } = this.microscopeViewer.GetState();
      const newViewport = new window.MicroscopeState(x, y, z + 0.5);
      this.microscopeViewer.AnimateTo(newViewport, 300);
      this.trackGAEvent("ZoomInButton");
    },

    // Zoom out one step
    zoomOut() {
      const { x, y, z } = this.microscopeViewer.GetState();
      const newViewport = new window.MicroscopeState(x, y, z - 0.5);
      this.microscopeViewer.AnimateTo(newViewport, 300);
      this.trackGAEvent("ZoomOutButton");
    },

    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
      // Track event
      if (this.isFullscreen) {
        this.trackGAEvent("EnterFullscreen");
      }
      // Wait to dom changes to kick in so the 3p viewer can calculate layout correctly
      Vue.nextTick(() => {
        // Tell 3p viewer to update layout
        this.microscopeViewer.Maximize();
        this.microscopeViewer.Center();
        this.microscopeViewer.ResizeViewport();
        this.onResize();
      });
    },

    // ESC key binding
    onEsc() {
      // Exit FS mode
      if (this.isFullscreen) {
        this.toggleFullscreen();
      }
    },

    // Get current component size
    getSize() {
      return {
        width: this.$el.clientWidth,
        height: this.$el.clientHeight
      };
    },

    // Get how much the viewer is zoomed
    getScale(viewport) {
      const zoomDelta = this.imageMetadata.maxZoomLevel - viewport.z;
      return (zoomDelta > 0) ? Math.pow(2, zoomDelta) : 1;
    },

    // Get the current image pan (x/y offset)
    getPan(viewport) {
      const mSize = {
        width: this.$refs.microscopeViewerEl.clientWidth,
        height: this.$refs.microscopeViewerEl.clientHeight
      };
      const w = mSize.width / viewport.size.width;
      const h = mSize.height / viewport.size.height;
      return {
        left: (mSize.width - w) / 2 + w * 0.5 - viewport.x * w,
        top: (mSize.height - h) / 2 + h * 0.5 - viewport.y * h
      };
    },

    // Handle zoom and viewport changes
    onViewportChange(viewport) {
      // Get scale
      const newScale = this.getScale(viewport);
      // Update component state if needed
      if (newScale != this.currentScale) {
        this.currentScale = newScale;
      }
      // Get pan
      const newPan = this.getPan(viewport);
      // Update component state if needed
      if (!equal(newPan, this.currentPan)) {
        this.currentPan = newPan;
      }

      // Update wether image is fully zoomed out
      this.isFullyZoomedOut =
        viewport.size.width >= 0.9 && viewport.size.height >= 0.9;

      this.isViewportReady = true;
      this.onInteractionComplete();
    },

    // Mousemove events over the entire component
    onMouseMove() {
      // Show extra UI
      this.isIdle = false;
      // Start idle timer
      this.onInteractionComplete();
    },

    // Mouse enter extra ui layer
    onExtrasMouseEnter() {
      this.mouseIsOverExtraUI = true;
    },

    // Mouse leave extra ui layer
    onExtrasMouseLeave() {
      this.mouseIsOverExtraUI = false;
    },

    // Debounce idle mode. Delay matches the 3p viewer timing
    onInteractionComplete: debounce(function() {
      this.onIdle();
    }, 1100),

    // Idle mode
    onIdle() {
      // Prevent idle mode if mouse is over a UI element
      if (!this.mouseIsOverExtraUI) {
        this.isIdle = true;
      }
    },

    // Handle double clicks
    onDoubleClick(event) {
      // Avoid native selection behavior
      event.preventDefault();
    },

    onTouchStart(event) {
      this.hasTouch = true;
      // We only want *real* touch events handled
      if (event.type == "click") {
        return;
      }

      // In case of single touch, we might want to prevent 3p viewer from catching the event.
      // If we don't, it initiates panning and prevents the default browser scroll behavior.
      if (event.targetTouches.length == 1) {
        if (!this.allowTouchPan) {
          event.stopPropagation();
        }
      }
    },

    onTouchMove(event) {
      // In case of single touch, we might want to prevent 3p viewer from catching the event.
      // If we don't, it initiates panning and prevents the default browser scroll behavior.
      if (event.targetTouches.length == 1) {
        if (!this.allowTouchPan) {
          this.isTouchScrolling = true;
          event.stopPropagation();
        }
      }
    },

    onTouchEnd() {
      if (!this.isTouchScrolling) {
        // Get out of idle mode
        this.onMouseMove();
      }
      this.isTouchScrolling = false;
    },

    // Resize handler
    onResize() {
      this.updateGlass();
    },

    // Implementation of allowing/blocking gestures to pan/zoom the 3p viewer
    updateMicroscopeScrollPolicy(value) {
      // Update the 3p viewer to ignore scroll events
      if (this.microscopeViewer) {
        this.microscopeViewer.SetScrollEventsActive(value);
      }
    },

    // Update internal 3p glass layer size.
    // Fixes a bug in 3p code
    updateGlass() {
      const {
        width,
        height
      } = this.$el.getBoundingClientRect();

      this.microscopeGlassElement.style.height = `${ height }px`;
      this.microscopeGlassElement.style.width = `${ width }px`;
    },

    // Google analytics event tracking
    trackGAEvent(action) {
      if (window._gaq) {
        window._gaq.push(["_trackEvent", "Viewer", action]);
      }
    }
  },

  template: require("./templates/image-viewer.html")
});



// WEBPACK FOOTER //
// ./src/frontend/common/image-viewer/image-viewer.js